import React, {
  useEffect,
  useCallback,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  MutableRefObject,
} from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import {
  GridApi,
  GridOptions,
  DisplayedColumnsChangedEvent,
  GridReadyEvent,
  GridSizeChangedEvent,
  FilterChangedEvent,
  RowDataUpdatedEvent,
  GetContextMenuItems,
  SortChangedEvent,
  DragStoppedEvent,
  RowClassParams,
  Column,
} from 'ag-grid-community';
import SdkAgGrid from '@tls/ui-aggrid';

import CheckboxGroupNativeSelectCellEditor from '../../columnDefinitions/cellRenderers/checkboxGroupNativeSelectCellEditor.component';
import { clientIdSelector } from '../../../shared/store/context';
import { setClientLocalStorageItem } from '../../localStorage';

import styles from './styles.module.scss';
import { setColumnStateByLocalStorage } from './utils';

const noop = () => null;

export const defaultGridWrapperId = 'grid-wrapper';

type CustomAgGridWrapperProps = {
  gridWrapperId?: string;
  saveGridStateId?: string;
  innerRef?: MutableRefObject<HTMLDivElement>;
  className?: string;
  minHeight?: string | number;
  autoMaxWidth?: boolean;
  shadeRows?: boolean;
  areHeaderCellBordersEnabled?: boolean;
  autoScrollPosition?: 'top' | 'middle' | 'bottom' | null;
  suppressColumnVirtualisation?: boolean;
  onGridReady?: (event: GridReadyEvent) => unknown;
  onGridSizeChanged?: (event: GridSizeChangedEvent) => unknown;
  onFilterChanged?: (event: FilterChangedEvent) => unknown;
  onDisplayedColumnsChanged?: (event: DisplayedColumnsChangedEvent) => unknown;
  onRowDataUpdated?: (event?: RowDataUpdatedEvent) => unknown;
  onSortChanged?: (event: SortChangedEvent) => void;
  getContextMenuItems?: GetContextMenuItems;
  /** When true, skip wrapper resize/setState on row data updates (avoids killing popup editors like agRichSelect). */
  suppressResizeGridOnRowDataUpdated?: boolean;
};

type CustomAgGridGridProps = Omit<
  GridOptions,
  'onSortChanged' | 'suppressResizeGridOnRowDataUpdated'
>;

type CustomAgGridProps = CustomAgGridWrapperProps & CustomAgGridGridProps & Record<string, unknown>;

interface CustomAgGridHandle {
  handleResize: () => void;
}

// disabling because when using `forwardRef`, it isn't automatically detected that we don't need
// prop types when using TS types/interfaces
const AgGrid = forwardRef<CustomAgGridHandle, CustomAgGridProps>((props, ref) => {
  const {
    columnDefs,
    gridWrapperId = defaultGridWrapperId,
    saveGridStateId,
    innerRef = undefined,
    className = '',
    onGridReady = undefined,
    minHeight,
    autoMaxWidth,
    shadeRows,
    onGridSizeChanged = noop,
    onFilterChanged = noop,
    onDisplayedColumnsChanged = noop,
    onRowDataUpdated = noop,
    onSortChanged,
    areHeaderCellBordersEnabled = false,
    // ! warning: by setting the above to `true`, you won't be able to use `context` property of
    // ! `HeaderClassParams` so in such case:
    // ! headerClass: (params) => ...
    // ! params.context will always be `undefined`
    autoScrollPosition = 'bottom',
    getContextMenuItems = null,
    rowBuffer,
    suppressColumnVirtualisation = true,
    rowDragManaged,
    rowDragMultiRow,
    animateRows = true,
    suppressResizeGridOnRowDataUpdated = false,
  } = props as CustomAgGridWrapperProps & CustomAgGridGridProps;

  const suppressResizeGridOnRowDataUpdatedRef = useRef(false);
  suppressResizeGridOnRowDataUpdatedRef.current = suppressResizeGridOnRowDataUpdated ?? false;

  const sdkGridProps = { ...props } as CustomAgGridProps;
  delete sdkGridProps.suppressResizeGridOnRowDataUpdated;
  delete sdkGridProps.onSortChanged;

  const onSortChangedCallbackRef = useRef(onSortChanged);
  onSortChangedCallbackRef.current = onSortChanged;

  const clientId = useSelector(clientIdSelector);
  const gridRef = useRef<HTMLDivElement>(null);
  const el: MutableRefObject<HTMLDivElement | null> = innerRef ?? gridRef;

  const [firstDataRendered, setFirstDataRendered] = useState<boolean>(false);
  const [maxWidth, setMaxWidth] = useState(0);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  useEffect(() => {
    setColumnStateByLocalStorage({
      clientId,
      saveGridStateId,
      gridApi,
    });
  }, [columnDefs, clientId, saveGridStateId, gridApi]);

  const onFirstDataRendered = useCallback(() => {
    setFirstDataRendered(true);
  }, []);

  const saveGridState = useCallback(
    ({ api }: FilterChangedEvent | SortChangedEvent | DragStoppedEvent | { api: GridApi }) => {
      if (!saveGridStateId || !firstDataRendered) {
        return;
      }

      const columns = api.getColumnState();
      const filters = api.getFilterModel();

      setClientLocalStorageItem({
        clientId,
        clientDetailKey: saveGridStateId,
        data: {
          filters,
          columns,
        },
      });
    },
    [clientId, saveGridStateId, firstDataRendered],
  );

  const onAgGridReady = useCallback(
    (event: GridReadyEvent) => {
      setGridApi(event.api);

      if (onGridReady && typeof onGridReady === 'function') {
        onGridReady(event);
      }
    },
    [onGridReady],
  );

  const resizeGrid = useCallback(() => {
    if (!el.current || !gridApi) {
      return;
    }

    // Defer layout update to next frame to avoid "ResizeObserver loop completed with undelivered notifications"
    // (setState during resize callback can trigger the loop)
    const runResize = () => {
      const agBodyViewport = el.current?.querySelector<HTMLDivElement>('.ag-body-viewport');
      if (!el.current || !gridApi || !agBodyViewport) {
        return;
      }

      const containerWidth = el.current.offsetWidth || el.current.clientWidth;
      const allColumns = gridApi.getAllGridColumns() ?? null;
      const gridWidth =
        allColumns && allColumns.length > 0
          ? allColumns.reduce((total: number, col: Column) => {
              return total + (col.getActualWidth() || 200);
            }, 0)
          : null;
      const hasVerticalScrollbar = agBodyViewport.scrollHeight > agBodyViewport.offsetHeight;
      if (containerWidth > 0) {
        if (!autoMaxWidth) {
          if (gridWidth && gridWidth > 0) {
            const scrollbarWidth = 17;
            if (hasVerticalScrollbar) {
              setMaxWidth(Number(gridWidth) + scrollbarWidth);
            } else {
              setMaxWidth(gridWidth);
            }
          } else {
            setMaxWidth(containerWidth);
          }
        }
      }
    };

    requestAnimationFrame(runResize);
  }, [gridApi, setMaxWidth, el, autoMaxWidth]);

  const handleRowDataUpdated = useCallback(
    (event?: RowDataUpdatedEvent) => {
      if (!suppressResizeGridOnRowDataUpdatedRef.current) {
        resizeGrid();
      }
      if (onRowDataUpdated && typeof onRowDataUpdated === 'function') {
        onRowDataUpdated(event);
      }
    },
    [resizeGrid, onRowDataUpdated],
  );

  const onAgGridResizeChange = useCallback(
    (event: GridSizeChangedEvent) => {
      resizeGrid();
      if (typeof onGridSizeChanged === 'function') {
        onGridSizeChanged(event);
      }

      const selectedNode = event.api.getSelectedNodes()[0];
      if (selectedNode) {
        event.api.ensureNodeVisible(
          selectedNode,
          autoScrollPosition as 'top' | 'bottom' | 'middle' | null,
        );
      }
    },
    [resizeGrid, onGridSizeChanged, autoScrollPosition],
  );

  const handleFilterChanged = useCallback(
    (event: FilterChangedEvent) => {
      resizeGrid();
      if (typeof onFilterChanged === 'function') {
        onFilterChanged(event);
      }
      saveGridState(event);
    },
    [resizeGrid, onFilterChanged, saveGridState],
  );

  const handleReset = useCallback(() => {
    resizeGrid();
    if (gridApi) {
      saveGridState({ api: gridApi });
    }
  }, [resizeGrid, saveGridState, gridApi]);

  useImperativeHandle(
    ref,
    () => ({
      handleResize: resizeGrid,
    }),
    [resizeGrid],
  );

  const getRowClass = useCallback(
    (params: RowClassParams) => shadeRows && Number(params.node.rowIndex) % 2 === 1 && 'shaded-row',
    [shadeRows],
  );

  const handleDisplayedColumnsChanged = useCallback(
    (event: DisplayedColumnsChangedEvent) => {
      if (typeof onDisplayedColumnsChanged === 'function') {
        onDisplayedColumnsChanged(event);
      }
    },
    [onDisplayedColumnsChanged],
  );

  const handleSortChanged = useCallback(
    (event: SortChangedEvent) => {
      saveGridState(event);
      onSortChangedCallbackRef.current?.(event);
    },
    [saveGridState],
  );

  const mergedGridComponents = {
    ...((sdkGridProps as { components?: Record<string, unknown> }).components ?? {}),
    CheckboxGroupNativeSelectCellEditor,
  };

  return (
    <div
      id={gridWrapperId}
      ref={el}
      className={classNames(
        'grid-auto-sizing-wrapper',
        {
          [styles.headerBorders]: areHeaderCellBordersEnabled,
        },
        className,
      )}
      style={{
        maxWidth: autoMaxWidth ? 'auto' : maxWidth > 0 ? maxWidth : undefined,
        margin: !autoMaxWidth && maxWidth > 0 ? '0 auto' : undefined,
        minHeight,
        width: '100%',
        height: '100%',
      }}
    >
      <SdkAgGrid
        suppressSizeColumnsToFit
        suppressColumnVirtualisation={suppressColumnVirtualisation}
        reactiveCustomComponents={false}
        {...sdkGridProps}
        components={mergedGridComponents}
        onGridReady={onAgGridReady}
        rowBuffer={rowBuffer || 0}
        onGridSizeChanged={onAgGridResizeChange}
        onRowDataUpdated={handleRowDataUpdated}
        onFilterChanged={handleFilterChanged}
        onDisplayedColumnsChanged={handleDisplayedColumnsChanged}
        getRowClass={getRowClass}
        createCustomContextMenu={getContextMenuItems}
        onSortChanged={handleSortChanged}
        onDragStopped={saveGridState}
        onColumnVisible={saveGridState}
        onReset={handleReset}
        // onFirstDataRendered means the grid is fully ready
        onFirstDataRendered={onFirstDataRendered}
        // this property prevents "flashing" the content during the vertical scroll
        suppressAnimationFrame
        animateRows={animateRows}
        rowDragManaged={rowDragManaged}
        rowDragMultiRow={rowDragMultiRow}
      />
    </div>
  );
});

AgGrid.displayName = 'AgGrid';

export default AgGrid;
